import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HotTable } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.min.css';
import { COLORS } from '../App';
import { apiFetch } from '../api';
import { runUnderwriting, getUnderwritingRuns } from '../api';
import UWComparison from './UWComparison';

registerAllModules();

export default function Underwriting({ deal, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lenderItems, setLenderItems] = useState([]);
  const [fitchItems, setFitchItems] = useState([]);
  const [savingCells, setSavingCells] = useState({});
  const [modifiedCells, setModifiedCells] = useState({});
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('assumptions'); // 'assumptions' | 'worksheet'
  const [worksheetRows, setWorksheetRows] = useState([]);
  const [footnotes, setFootnotes] = useState({});
  const [selectedCell, setSelectedCell] = useState(null);
  const [generatingFootnotes, setGeneratingFootnotes] = useState(false);
  const [rates, setRates] = useState(null);
  const debounceTimers = useRef({});
  const hotRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [assumptRes, runsRes] = await Promise.all([
        apiFetch(`/underwriting/deal-assumptions/${deal.id}`),
        getUnderwritingRuns(deal.id).catch(() => ({ data: [] })),
      ]);
      if (assumptRes.success) {
        setLenderItems(assumptRes.data.lender_standard || []);
        setFitchItems(assumptRes.data.fitch_conservative || []);
      } else {
        setError(assumptRes.error || 'Failed to load assumptions');
      }
      setRuns((runsRes.data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [deal.id]);

  const loadWorksheet = useCallback(async () => {
    try {
      const [wsRes, fnRes, ratesRes] = await Promise.all([
        apiFetch(`/underwriting/worksheet/${deal.id}`),
        apiFetch(`/underwriting/footnotes/${deal.id}`),
        apiFetch('/rates').catch(() => ({ success: false })),
      ]);
      if (wsRes.success) setWorksheetRows(wsRes.data.rows || []);
      if (fnRes.success) {
        const fnMap = {};
        for (const fn of fnRes.data) fnMap[fn.cell_ref] = fn.footnote_text;
        setFootnotes(fnMap);
      }
      if (ratesRes.success) setRates(ratesRes.data);
    } catch (e) {
      console.warn('Worksheet load error:', e.message);
    }
  }, [deal.id]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (activeTab === 'worksheet') loadWorksheet();
  }, [activeTab, loadWorksheet]);

  const handleChange = (set, index, displayValue) => {
    const list = set === 'lender' ? lenderItems : fitchItems;
    const setter = set === 'lender' ? setLenderItems : setFitchItems;
    const item = list[index];
    const rawValue = displayToRaw(displayValue, item.unit);

    const updated = list.map((it, i) => i === index ? { ...it, value: rawValue } : it);
    setter(updated);
    setModifiedCells(prev => ({ ...prev, [item.cell_ref]: true }));

    clearTimeout(debounceTimers.current[item.cell_ref]);
    debounceTimers.current[item.cell_ref] = setTimeout(async () => {
      setSavingCells(prev => ({ ...prev, [item.cell_ref]: true }));
      try {
        await apiFetch(`/underwriting/deal-assumptions/${deal.id}`, {
          method: 'PUT',
          body: { cell_ref: item.cell_ref, value: rawValue },
        });
      } catch (e) {
        console.error('Save failed:', e);
      }
      setSavingCells(prev => ({ ...prev, [item.cell_ref]: false }));
    }, 500);
  };

  const handleReset = async () => {
    try {
      await apiFetch(`/underwriting/deal-assumptions/${deal.id}/reset`, { method: 'POST' });
      setModifiedCells({});
      loadData();
    } catch (e) {
      alert('Reset failed: ' + e.message);
    }
  };

  const handleRun = async (runType) => {
    setRunning(runType);
    try {
      await runUnderwriting(deal.id, runType);
      await loadData();
      if (activeTab === 'worksheet') loadWorksheet();
      onUpdate();
    } catch (e) {
      alert('Underwriting failed: ' + e.message);
    }
    setRunning(false);
  };

  const handleGenerateFootnotes = async () => {
    setGeneratingFootnotes(true);
    try {
      const res = await apiFetch(`/underwriting/footnotes/${deal.id}/generate`, { method: 'POST' });
      if (res.success) {
        const fnMap = {};
        for (const fn of res.data) fnMap[fn.cell_ref] = fn.footnote_text;
        setFootnotes(prev => ({ ...prev, ...fnMap }));
      }
    } catch (e) {
      alert('Footnote generation failed: ' + e.message);
    }
    setGeneratingFootnotes(false);
  };

  const handleFootnoteEdit = async (cellRef, text) => {
    setFootnotes(prev => ({ ...prev, [cellRef]: text }));
    try {
      await apiFetch(`/underwriting/footnotes/${deal.id}/${cellRef}`, {
        method: 'PUT',
        body: { footnote_text: text },
      });
    } catch (e) {
      console.error('Footnote save failed:', e);
    }
  };

  // Display formatting
  function rawToDisplay(value, unit) {
    if (value === null || value === undefined || isNaN(value)) return '';
    if (unit === 'percent') return (value * 100).toFixed(2);
    if (unit === 'dollar_psf') return value.toFixed(2);
    if (unit === 'multiple') return value.toFixed(2);
    if (unit === 'months') return String(Math.round(value));
    return String(value);
  }

  function displayToRaw(str, unit) {
    const num = parseFloat(String(str).replace(/[^0-9.\-]/g, ''));
    if (isNaN(num)) return 0;
    if (unit === 'percent') return num / 100;
    return num;
  }

  function unitSuffix(unit) {
    if (unit === 'percent') return '%';
    if (unit === 'dollar_psf') return ' /SF';
    if (unit === 'multiple') return 'x';
    if (unit === 'months') return ' mo';
    return '';
  }

  // Build Handsontable data from worksheet rows
  const buildHotData = () => {
    if (!worksheetRows.length) return [];
    const maxCol = Math.max(...worksheetRows.map(r => Math.max(...r.cells.map(c => c.col), 0)));
    return worksheetRows.map(row => {
      const arr = new Array(maxCol).fill('');
      for (const cell of row.cells) {
        let v = cell.value;
        if (v === null || v === undefined) v = '';
        else if (typeof v === 'number') v = Math.round(v * 100) / 100;
        arr[cell.col - 1] = v;
      }
      return arr;
    });
  };

  const hotData = buildHotData();

  const card = { background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, overflow: 'hidden', flex: 1 };
  const formatPct = v => v != null ? (v * 100).toFixed(2) + '%' : 'N/A';
  const formatCurrency = v => v != null ? '$' + Number(v).toLocaleString() : 'N/A';
  const latestRun = runs[0] || null;

  function renderAssumptionCard(title, items, set, headerBg) {
    return (
      <div style={card}>
        <div style={{ background: headerBg, color: '#FFFFFF', padding: '12px 18px', fontFamily: 'Georgia, serif', fontSize: 13, fontWeight: 700, letterSpacing: 0.5 }}>
          {title}
        </div>
        <div style={{ padding: '8px 16px' }}>
          {items.map((item, idx) => {
            const modified = modifiedCells[item.cell_ref];
            const saving = savingCells[item.cell_ref];
            return (
              <div key={item.cell_ref} style={{ display: 'flex', alignItems: 'center', padding: '7px 0', borderBottom: idx < items.length - 1 ? `1px solid ${COLORS.border}` : 'none' }}>
                <div style={{ flex: 1, fontSize: 12, color: COLORS.text, fontWeight: 500 }}>
                  {item.label}
                  {modified && (
                    <span title="Modified" style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: COLORS.gold, marginLeft: 6, verticalAlign: 'middle' }} />
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="text"
                    value={rawToDisplay(item.value, item.unit)}
                    onChange={e => handleChange(set, idx, e.target.value)}
                    style={{ width: 72, padding: '5px 7px', border: `1px solid ${modified ? COLORS.gold : COLORS.border}`, borderRadius: 4, fontSize: 12, textAlign: 'right', fontFamily: 'monospace', color: COLORS.navy }}
                  />
                  <span style={{ fontSize: 11, color: COLORS.textLight, minWidth: 22 }}>{unitSuffix(item.unit)}</span>
                  {saving && <span style={{ fontSize: 10, color: COLORS.gold }}>...</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: COLORS.textLight }}>Loading assumptions...</div>;

  if (error) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ color: '#C62828', marginBottom: 12, fontSize: 14 }}>{error}</div>
      <div style={{ color: COLORS.textLight, fontSize: 13 }}>Run "Populate Model" (Step 3) first to generate the underwriting file with default assumptions.</div>
    </div>
  );

  return (
    <div>
      {/* Rates banner */}
      {rates && (
        <div style={{ background: '#F0F4FF', border: `1px solid #C7D2FE`, borderRadius: 6, padding: '8px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.navy, letterSpacing: 0.5 }}>LIVE RATES</span>
          {rates.sofr != null && (
            <span style={{ fontSize: 12, color: COLORS.text }}>SOFR: <strong>{(rates.sofr * 100).toFixed(3)}%</strong></span>
          )}
          {rates.treasury?.['5yr'] != null && (
            <span style={{ fontSize: 12, color: COLORS.text }}>UST 5Y: <strong>{(rates.treasury['5yr'] * 100).toFixed(3)}%</strong></span>
          )}
          {rates.treasury?.['10yr'] != null && (
            <span style={{ fontSize: 12, color: COLORS.text }}>UST 10Y: <strong>{(rates.treasury['10yr'] * 100).toFixed(3)}%</strong></span>
          )}
          <span style={{ fontSize: 11, color: COLORS.textLight, marginLeft: 'auto' }}>
            Updated {rates.fetchedAt ? new Date(rates.fetchedAt).toLocaleTimeString() : '—'}
          </span>
        </div>
      )}

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: `2px solid ${COLORS.border}` }}>
        {[
          { key: 'assumptions', label: 'Assumptions' },
          { key: 'worksheet', label: 'UW Worksheet' },
          { key: 'comparison', label: 'UW Comparison' },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '9px 20px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: activeTab === t.key ? 700 : 500,
            color: activeTab === t.key ? COLORS.navy : COLORS.textLight,
            borderBottom: activeTab === t.key ? `2px solid ${COLORS.navy}` : '2px solid transparent',
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'assumptions' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, margin: 0 }}>Underwriting Assumptions</h3>
            <p style={{ color: COLORS.textLight, fontSize: 13, margin: '4px 0 0' }}>
              Defaults loaded from the underwriting model template. Changes save automatically to the populated model.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            {renderAssumptionCard('LENDER STANDARD ASSUMPTIONS', lenderItems, 'lender', COLORS.navy)}
            {renderAssumptionCard('FITCH CONSERVATIVE ASSUMPTIONS', fitchItems, 'fitch', '#8B6914')}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <button onClick={handleReset} style={{ padding: '8px 16px', background: 'transparent', color: COLORS.textLight, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
              Reset to Template Defaults
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => handleRun('lender_standard')} disabled={!!running} style={{ padding: '11px 24px', background: COLORS.navy, color: '#FFFFFF', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: running === 'lender_standard' ? 0.7 : 1 }}>
                {running === 'lender_standard' ? 'Running...' : 'Run Lender UW →'}
              </button>
              <button onClick={() => handleRun('fitch_conservative')} disabled={!!running} style={{ padding: '11px 24px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: running === 'fitch_conservative' ? 0.7 : 1 }}>
                {running === 'fitch_conservative' ? 'Running...' : 'Run Fitch Stress →'}
              </button>
            </div>
          </div>

          {latestRun && latestRun.results && (
            <div style={{ background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 20, marginBottom: 16 }}>
              <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, margin: '0 0 16px' }}>
                Results — {latestRun.run_type === 'lender_standard' ? 'Lender Standard' : 'Fitch Conservative'}
                <span style={{ fontFamily: 'inherit', fontWeight: 400, fontSize: 12, color: COLORS.textLight, marginLeft: 12 }}>
                  {new Date(latestRun.created_at).toLocaleString()}
                </span>
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                {[
                  { label: 'NOI', value: formatCurrency(latestRun.results.noi), pass: null },
                  { label: 'DSCR', value: latestRun.results.dscr ? latestRun.results.dscr.toFixed(2) + 'x' : 'N/A', pass: latestRun.results.dscr >= 1.25 },
                  { label: 'Debt Yield', value: formatPct(latestRun.results.debt_yield), pass: latestRun.results.debt_yield >= 0.09 },
                  { label: 'LTV', value: formatPct(latestRun.results.ltv), pass: latestRun.results.ltv <= 0.70 },
                  { label: 'Cap Rate', value: formatPct(latestRun.results.cap_rate), pass: null },
                  { label: 'Property Value', value: formatCurrency(latestRun.results.property_value), pass: null },
                  { label: 'Eff. Gross Income', value: formatCurrency(latestRun.results.effective_gross_income), pass: null },
                  { label: 'Annual Debt Service', value: formatCurrency(latestRun.results.annual_debt_service), pass: null },
                ].map(({ label, value, pass }) => (
                  <div key={label} style={{ padding: 14, borderRadius: 6, border: `1px solid ${COLORS.border}`, textAlign: 'center', background: pass === true ? '#F0FFF4' : pass === false ? '#FFF5F5' : COLORS.white }}>
                    <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 6 }}>{label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: pass === true ? '#2E7D32' : pass === false ? '#C62828' : COLORS.navy }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {runs.length > 1 && (
            <div style={{ background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 20 }}>
              <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, margin: '0 0 12px' }}>Run History</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                    {['Type', 'NOI', 'DSCR', 'Debt Yield', 'LTV', 'Date'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {runs.map(r => (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: '7px 10px', fontSize: 12 }}>{r.run_type === 'lender_standard' ? 'Lender' : 'Fitch'}</td>
                      <td style={{ padding: '7px 10px', fontSize: 12 }}>{formatCurrency(r.results?.noi)}</td>
                      <td style={{ padding: '7px 10px', fontSize: 12 }}>{r.results?.dscr?.toFixed(2)}x</td>
                      <td style={{ padding: '7px 10px', fontSize: 12 }}>{formatPct(r.results?.debt_yield)}</td>
                      <td style={{ padding: '7px 10px', fontSize: 12 }}>{formatPct(r.results?.ltv)}</td>
                      <td style={{ padding: '7px 10px', fontSize: 11, color: COLORS.textLight }}>{new Date(r.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'comparison' && <UWComparison deal={deal} />}

      {activeTab === 'worksheet' && (
        <div style={{ display: 'flex', gap: 16 }}>
          {/* Main worksheet */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, margin: 0 }}>Lender Underwriting Worksheet</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleGenerateFootnotes} disabled={generatingFootnotes} style={{ padding: '7px 14px', background: COLORS.white, color: COLORS.navy, border: `1px solid ${COLORS.navy}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', opacity: generatingFootnotes ? 0.6 : 1 }}>
                  {generatingFootnotes ? 'Generating...' : 'Generate Footnotes'}
                </button>
                <button onClick={() => handleRun('lender_standard')} disabled={!!running} style={{ padding: '7px 14px', background: COLORS.navy, color: '#FFF', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: running ? 0.7 : 1 }}>
                  {running === 'lender_standard' ? 'Running...' : 'Run Lender UW'}
                </button>
              </div>
            </div>

            {worksheetRows.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: COLORS.textLight, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: COLORS.white }}>
                No worksheet data. Run underwriting first.
              </div>
            ) : (
              <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden' }}>
                <HotTable
                  ref={hotRef}
                  data={hotData}
                  readOnly={true}
                  colHeaders={false}
                  rowHeaders={false}
                  width="100%"
                  height={520}
                  stretchH="all"
                  licenseKey="non-commercial-and-evaluation"
                  afterSelection={(r, c) => {
                    const row = worksheetRows[r];
                    if (row) {
                      const cell = row.cells.find(cl => cl.col === c + 1);
                      if (cell) setSelectedCell(cell.ref);
                    }
                  }}
                  cells={(row) => {
                    const wsRow = worksheetRows[row];
                    if (!wsRow) return {};
                    // Bold section headers (single non-empty cell in col B)
                    const bCell = wsRow.cells.find(c => c.col === 2);
                    const hasOnlyB = wsRow.cells.filter(c => c.value).length <= 1 && bCell;
                    return hasOnlyB ? { className: 'htBold htMiddle' } : {};
                  }}
                  columnSorting={false}
                  manualColumnResize={true}
                />
              </div>
            )}
          </div>

          {/* Footnotes sidebar */}
          <div style={{ width: 280, flexShrink: 0 }}>
            <div style={{ background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 16 }}>
              <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, margin: '0 0 12px', fontSize: 14 }}>
                Analyst Footnotes
              </h4>
              {selectedCell && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>
                    Cell {selectedCell}
                  </div>
                  <textarea
                    value={footnotes[selectedCell] || ''}
                    onChange={e => handleFootnoteEdit(selectedCell, e.target.value)}
                    placeholder="Add underwriting rationale for this cell..."
                    style={{
                      width: '100%', boxSizing: 'border-box', height: 100, padding: '8px 10px',
                      border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12,
                      fontFamily: 'inherit', resize: 'vertical', color: COLORS.text,
                    }}
                  />
                </div>
              )}
              {!selectedCell && (
                <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 16 }}>
                  Click a cell in the worksheet to add or view a footnote.
                </div>
              )}
              {Object.keys(footnotes).length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.navy, marginBottom: 8, letterSpacing: 0.5 }}>ALL FOOTNOTES</div>
                  {Object.entries(footnotes).map(([ref, text]) => (
                    <div key={ref} style={{ marginBottom: 10, padding: '8px 10px', background: '#F9FAFB', borderRadius: 6, border: `1px solid ${COLORS.border}`, cursor: 'pointer' }}
                      onClick={() => setSelectedCell(ref)}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.navy, marginBottom: 3 }}>{ref}</div>
                      <div style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.4 }}>{text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
